import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  Card,
  CardBody,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { FiMail, FiLock, FiShield } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const Login = () => {
  const [email, setEmail] = useState('admin@sp-proposal.com');
  const [password, setPassword] = useState('admin123');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, clearAllTranslationCache } = useLanguage();
  const navigate = useNavigate();
  const toast = useToast();

  // Login sayfasına girildiğinde otomatik olarak tüm dil cache'lerini temizle
  useEffect(() => {
    clearAllTranslationCache();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast({
        title: t('common.success'),
        description: t('auth.login_success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/dashboard');
    } else {
      toast({
        title: t('common.error'),
        description: result.error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setLoading(false);
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, blue.600, blue.700, purple.700)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Container maxW="md">
        {/* Logo ve Başlık */}
        <VStack spacing={6} mb={8} textAlign="center">
          <Box
            p={4}
            borderRadius="full"
            bg="white"
            color="blue.600"
            boxShadow="2xl"
            border="3px solid white"
          >
            <Icon as={FiShield} boxSize={8} />
          </Box>
          <VStack spacing={2}>
            <Heading size="xl" color="white" fontWeight="bold" textShadow="0 2px 4px rgba(0,0,0,0.3)">
              {t('app.title', {defaultValue: 'SP Proposal Management'})}
            </Heading>
            <Text color="blue.50" fontSize="lg" fontWeight="medium">
              {t('auth.login_subtitle', {defaultValue: 'Login to your account'})}
            </Text>
          </VStack>
        </VStack>

        {/* Login Formu */}
        <Card
          bg="white"
          borderRadius="2xl"
          boxShadow="2xl"
          overflow="hidden"
          border="2px solid"
          borderColor="blue.100"
        >
          <CardBody p={8}>
            <VStack spacing={6}>
              <Box as="form" onSubmit={handleSubmit} w="100%">
                <VStack spacing={5}>
                  <FormControl isRequired>
                    <FormLabel color="gray.700" fontWeight="semibold">
                      {t('auth.email')}
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement color="gray.400">
                        <Icon as={FiMail} />
                      </InputLeftElement>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.email_placeholder')}
                        size="md"
                        border="2px"
                        borderColor="gray.300"
                        bg="gray.50"
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          bg: 'white'
                        }}
                        _hover={{
                          borderColor: 'blue.400',
                          bg: 'white'
                        }}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color="gray.700" fontWeight="semibold">
                      {t('auth.password')}
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement color="gray.400">
                        <Icon as={FiLock} />
                      </InputLeftElement>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.password_placeholder')}
                        size="md"
                        border="2px"
                        borderColor="gray.300"
                        bg="gray.50"
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          bg: 'white'
                        }}
                        _hover={{
                          borderColor: 'blue.400',
                          bg: 'white'
                        }}
                      />
                    </InputGroup>
                  </FormControl>

                  <Button
                    type="submit"
                    bgGradient="linear(to-r, blue.500, indigo.600)"
                    _hover={{
                      bgGradient: 'linear(to-r, blue.600, indigo.700)',
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    _active={{
                      transform: 'translateY(0)'
                    }}
                    size="md"
                    w="100%"
                    borderRadius="lg"
                    color="white"
                    fontWeight="bold"
                    fontSize="sm"
                    py={6}
                    transition="all 0.2s"
                  >
                    {t('auth.login')}
                  </Button>
                </VStack>
              </Box>

              {/* Demo Hesap Bilgileri */}
              <Box
                bg="blue.50"
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor="blue.200"
                w="100%"
                textAlign="center"
              >
                <Text fontSize="sm" color="blue.600" fontWeight="medium" mb={2}>
                  {t('auth.demo_account_info')}
                </Text>
                <VStack spacing={1}>
                  <Text fontSize="sm" color="blue.700">
                    <strong>Admin:</strong> admin@sp-proposal.com — <strong>{t('auth.password')}:</strong> admin123
                  </Text>
                  <Text fontSize="sm" color="blue.700">
                    <strong>CEO:</strong> ceo@sp-proposal.com — <strong>{t('auth.password')}:</strong> admin123
                  </Text>
                  <Text fontSize="sm" color="blue.700">
                    <strong>Manager:</strong> manager@sp-proposal.com — <strong>{t('auth.password')}:</strong> admin123
                  </Text>
                  <Text fontSize="sm" color="blue.700">
                    <strong>User:</strong> user@sp-proposal.com — <strong>{t('auth.password')}:</strong> admin123
                  </Text>
                </VStack>
              </Box>
              
              {/* Language Selector - Küçük boyut */}
              <Box w="100%" textAlign="center">
                <LanguageSelector size="sm" showLabel={false} />
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Alt Bilgi */}
        <Text 
          fontSize="sm" 
          color="white" 
          textAlign="center" 
          mt={6}
          fontWeight="medium"
          textShadow="0 1px 2px rgba(0,0,0,0.3)"
        >
          {t('common.copyright')}
        </Text>
      </Container>
    </Box>
  );
};

export default Login;
